import {
  Children,
  isValidElement,
  JSXElementConstructor,
  MouseEvent,
  ReactElement,
  ReactNode,
  useId,
  useMemo,
  useState
} from 'react'

import { Divider, Menu } from '@mui/material'
import { MenuButton } from '@/components/MenuButton'
import { useAuthStore } from '@/stores/useAuthStore'
import { resetCheckboxTableStore, useTableCheckboxStore } from '@/stores/useTableCheckboxStore'

import { CheckboxDeselectAllItem } from '../CheckboxDeselectAllItem/CheckboxDeselectAllItem'
import {
  CheckboxMenuContextProvider,
  type CheckboxMenuContextType
} from '../context/CheckboxMenuContext'
import { CheckboxMenuItemLike } from '../index.types'

type Props = {
  minimumToOpen?: number
  onOpen?: () => void
  onClose?: () => void
  children?:
    | ReactElement<CheckboxMenuItemLike, JSXElementConstructor<CheckboxMenuItemLike>>
    | ReactElement<CheckboxMenuItemLike, JSXElementConstructor<CheckboxMenuItemLike>>[]
  /** Table ID to connected table state with zustand */
  tableId: string
}

const DEFAULT_MINIMUM_TO_OPEN = 2
export const CHECKBOX_MENU_BUTTON_TEST_ID = 'checkbox-menu-button'

export const CheckboxMenu = <Data extends Record<string, unknown>>({
  onClose,
  onOpen,
  tableId,
  children,
  minimumToOpen = DEFAULT_MINIMUM_TO_OPEN
}: Props) => {
  const id = useId()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const platform = useAuthStore((state) => state.platform)
  const open = Boolean(anchorEl)
  const selectedData = useTableCheckboxStore((state) => state[tableId || '']) as
    | Array<Data & { __id: string }>
    | undefined

  const handleDeselectAll = () => {
    resetCheckboxTableStore(tableId)
  }

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
    onOpen?.()
  }

  const handleClose = () => {
    setAnchorEl(null)
    onClose?.()
  }

  const contextValue: CheckboxMenuContextType | undefined = useMemo(
    () =>
      selectedData
        ? {
            rows: selectedData,
            closeMenu: handleClose,
            deselectAll: handleDeselectAll
          }
        : undefined,
    [selectedData, handleClose, children, tableId]
  )

  const menuItems = useMemo(() => {
    if (!children) return []
    const childrenArray = Children.toArray(children).filter((child) =>
      isValidElement(child)
    ) as ReactElement<CheckboxMenuItemLike, JSXElementConstructor<CheckboxMenuItemLike>>[]

    const filteredChildren = childrenArray.filter((child) => {
      const { platform: itemPlatform } = child.props
      if (!itemPlatform) return true
      if (Array.isArray(itemPlatform)) return itemPlatform.includes(platform)
      return itemPlatform === platform
    })

    const items: ReactNode[] = []
    filteredChildren.forEach((child, index) => {
      const isDividerBefore = child.props?.divider === 'before' && index > 0
      const isDividerAfter = child.props?.divider === 'after' && index < filteredChildren.length - 1

      if (isDividerBefore)
        items.push(<Divider key={`${child.key}-divider-before`} data-testid="divider-before" />)
      items.push(child)
      if (isDividerAfter)
        items.push(<Divider key={`${child.key}-divider-after`} data-testid="divider-after" />)
    })
    return items
  }, [children, platform])

  if (!selectedData || selectedData.length < minimumToOpen || !contextValue) return null

  return (
    <CheckboxMenuContextProvider value={contextValue}>
      <MenuButton
        onClick={handleClick}
        open={open}
        label={`Selected (${selectedData.length})`}
        testId={CHECKBOX_MENU_BUTTON_TEST_ID}
      />

      <Menu
        id={`${id}-menu`}
        MenuListProps={{
          'aria-labelledby': `${id}-button`
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { boxShadow: 3, borderRadius: 1, mt: 0.5 } } }}
      >
        {menuItems}
        {menuItems.length > 0 && <Divider />}
        <CheckboxDeselectAllItem />
      </Menu>
    </CheckboxMenuContextProvider>
  )
}
